import { useCallback, useEffect, useState } from "react";
import "@style/Admin.scss";
import {
  kpsiGet,
  readSavedCompanyId,
  saveCompanyId,
  type KpsiBootstrap,
  type KpsiCompanyOption,
} from "./kpsiApi";
import KpsiTestsList from "./KpsiTestsList";
import KpsiTestView from "./KpsiTestView";
import KpsiTestPrint from "./KpsiTestPrint";
import KpsiTestEdit from "./KpsiTestEdit";
import KpsiCompaniesList from "./KpsiCompaniesList";
import KpsiCompanyEdit from "./KpsiCompanyEdit";
import KpsiUsersList from "./KpsiUsersList";
import KpsiUserEdit from "./KpsiUserEdit";

type Nav =
  | { page: "tests" }
  | { page: "view"; id: number }
  | { page: "print"; id: number }
  | { page: "edit"; id?: number; companyId?: number }
  | { page: "companies" }
  | { page: "companyEdit"; id?: number }
  | { page: "users" }
  | { page: "userEdit"; id?: number };

interface Props {
  tabData?: {
    activeTab?: string;
    updateSpinner?: (show: boolean) => void;
    adminPerms?: number[];
  };
}

export default function AdminKpsiTab({ tabData }: Props) {
  const active = tabData?.activeTab === "kpsi";
  const [boot, setBoot] = useState<KpsiBootstrap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nav, setNav] = useState<Nav>({ page: "tests" });
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadBoot = useCallback(async () => {
    tabData?.updateSpinner?.(true);
    try {
      const data = await kpsiGet<KpsiBootstrap>("/Kpsi/Bootstrap");
      setBoot(data);
      setError(null);
      const saved = readSavedCompanyId();
      const companies = data.companies ?? [];
      const pick =
        (saved && companies.some((c) => c.id === saved) ? saved : null)
        ?? companies[0]?.id
        ?? null;
      setCompanyId(pick);
      if (pick) saveCompanyId(pick);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load KPSI");
    } finally {
      tabData?.updateSpinner?.(false);
    }
  }, [tabData]);

  useEffect(() => {
    if (active && !boot && !error) {
      void loadBoot();
    }
  }, [active, boot, error, loadBoot]);

  if (!active) return null;

  const companies: KpsiCompanyOption[] = boot?.companies ?? [];
  const isAdmin = !!boot?.isAdmin;

  function onCompanyChange(id: number) {
    setCompanyId(id);
    saveCompanyId(id);
  }

  function flash(msg: string) {
    setMessage(msg);
  }

  const isPrintPage = nav.page === "print";

  return (
    <div className={`kpsi-shell${isPrintPage ? " is-print" : ""}`}>
      <div className="kpsi-frame">
        {!isPrintPage && (
        <nav className="kpsi-nav kpsi-no-print">
          <button
            type="button"
            className={nav.page === "tests" || nav.page === "view" || nav.page === "edit" ? "active" : ""}
            onClick={() => setNav({ page: "tests" })}
          >
            KPSI Database
          </button>
          {isAdmin && (
            <>
              <button
                type="button"
                className={nav.page === "users" || nav.page === "userEdit" ? "active" : ""}
                onClick={() => setNav({ page: "users" })}
              >
                Manage Users
              </button>
              <button
                type="button"
                className={nav.page === "companies" || nav.page === "companyEdit" ? "active" : ""}
                onClick={() => setNav({ page: "companies" })}
              >
                Manage Companies
              </button>
            </>
          )}
        </nav>
        )}

        {message && (
          <div className="kpsi-alert kpsi-no-print">
            <span>{message}</span>
            <button type="button" onClick={() => setMessage(null)}>Dismiss</button>
          </div>
        )}
        {error && <div className="kpsi-alert">{error}</div>}

        {!boot && !error && <p className="kpsi-muted">Loading KPSI…</p>}

        {boot && nav.page === "tests" && companyId != null && (
          <KpsiTestsList
            companyId={companyId}
            companies={companies}
            isAdmin={isAdmin}
            onCompanyChange={onCompanyChange}
            onView={(id) => setNav({ page: "view", id })}
            onEdit={(id) => setNav({ page: "edit", id })}
            onAdd={() => setNav({ page: "edit", companyId })}
            onFlash={flash}
          />
        )}
        {boot && nav.page === "tests" && companyId == null && (
          <p>You do not have access to any KPSI companies yet. Ask an administrator to assign interpreter or scorer access.</p>
        )}
        {boot && nav.page === "view" && (
          <KpsiTestView
            id={nav.id}
            onBack={() => setNav({ page: "tests" })}
            onEdit={(id) => setNav({ page: "edit", id })}
            onPrint={(id) => setNav({ page: "print", id })}
            onFlash={flash}
          />
        )}
        {boot && nav.page === "print" && (
          <KpsiTestPrint
            id={nav.id}
            onNormalView={(id) => setNav({ page: "view", id })}
            onFlash={flash}
          />
        )}
        {boot && nav.page === "edit" && (
          <KpsiTestEdit
            id={nav.id}
            companyId={nav.companyId}
            onBack={() => setNav({ page: "tests" })}
            onSaved={(id) => {
              flash("Record saved.");
              setNav({ page: "view", id });
            }}
          />
        )}
        {boot && nav.page === "companies" && (
          <KpsiCompaniesList
            onEdit={(id) => setNav({ page: "companyEdit", id })}
            onAdd={() => setNav({ page: "companyEdit" })}
            onFlash={flash}
          />
        )}
        {boot && nav.page === "companyEdit" && (
          <KpsiCompanyEdit
            id={nav.id}
            onBack={() => setNav({ page: "companies" })}
            onSaved={() => {
              flash("Company saved.");
              setNav({ page: "companies" });
              void loadBoot();
            }}
          />
        )}
        {boot && nav.page === "users" && (
          <KpsiUsersList
            onEdit={(id) => setNav({ page: "userEdit", id })}
            onAdd={() => setNav({ page: "userEdit" })}
            onFlash={flash}
          />
        )}
        {boot && nav.page === "userEdit" && (
          <KpsiUserEdit
            id={nav.id}
            onBack={() => setNav({ page: "users" })}
            onSaved={() => {
              flash("User access saved.");
              setNav({ page: "users" });
            }}
          />
        )}
      </div>
    </div>
  );
}
